import {
  fetchAccountOwnedPlaylists,
  fetchProfileOwnedPlaylists,
  fetchProfileSpotifySession,
} from '../services/curatorAccountService';
import { resolveInitialProfilePlaylists, type LinkedCuratorProfile } from './accountCuratorOffer';
import type { CuratorOfferSubmissionPlaylist } from './curatorOffer';

export interface CampaignAcceptPlaylistOption {
  playlistId: string;
  name: string;
  profileId: string;
  profileUsername: string;
  imageUrl?: string;
  externalUrl?: string;
}

export interface ProfileSpotifySession {
  profileId: string;
  accessToken: string;
  spotifyUserId: string;
}

const getOpenOfferProfileIds = (linkedProfiles: LinkedCuratorProfile[]): string[] =>
  linkedProfiles.filter(profile => profile.acceptCampaigns).map(profile => profile.id);

const getCampaignAssignedProfileIds = (
  campaign: { assignedProfileIds?: string[]; curatorIds?: string[] },
  linkedProfiles: LinkedCuratorProfile[]
): string[] => {
  const accountIds = new Set(linkedProfiles.map(profile => profile.id));
  const assignedIds = (campaign.assignedProfileIds || campaign.curatorIds || [])
    .map(id => id?.toString())
    .filter(id => accountIds.has(id));

  return Array.from(new Set(assignedIds));
};

/** Profiles both assigned to the campaign and currently enabled in the offer. */
const getCampaignEligibleProfileIds = (
  campaign: { assignedProfileIds?: string[]; curatorIds?: string[] },
  linkedProfiles: LinkedCuratorProfile[]
): string[] => {
  const assignedIds = getCampaignAssignedProfileIds(campaign, linkedProfiles);
  const openOfferIds = new Set(getOpenOfferProfileIds(linkedProfiles));

  return assignedIds.filter(profileId => openOfferIds.has(profileId));
};

export const filterCampaignsToOpenOfferProfiles = <
  T extends {
    assignedProfileIds?: string[];
    curatorIds?: string[];
    acceptedById?: string;
    rejectedByIds?: string[];
  },
>(
  campaigns: T[],
  linkedProfiles: LinkedCuratorProfile[],
  activeProfileId?: string | null
): T[] => {
  if (!activeProfileId) {
    return [];
  }

  const activeProfile = linkedProfiles.find(profile => profile.id === activeProfileId);
  if (!activeProfile) {
    return [];
  }

  const activeProfileParticipated = (campaign: T) => {
    const acceptedById = campaign.acceptedById?.toString?.();
    const rejectedByIds = (campaign.rejectedByIds || []).map(id => id?.toString());
    return acceptedById === activeProfileId || rejectedByIds.includes(activeProfileId);
  };

  return campaigns.filter(campaign => {
    const assignedIds = getCampaignAssignedProfileIds(campaign, linkedProfiles);
    const isAssignedToActiveProfile = assignedIds.includes(activeProfileId);

    if (!isAssignedToActiveProfile) {
      return activeProfileParticipated(campaign);
    }

    if (activeProfile.acceptCampaigns) {
      return true;
    }

    return activeProfileParticipated(campaign);
  });
};

export const shouldShowCampaignsForActiveProfile = (
  linkedProfiles: LinkedCuratorProfile[],
  activeProfileId?: string | null
): boolean => {
  if (!activeProfileId) {
    return getOpenOfferProfileIds(linkedProfiles).length > 0;
  }

  const activeProfile = linkedProfiles.find(profile => profile.id === activeProfileId);
  return activeProfile?.acceptCampaigns === true;
};

export const resolveActingProfileId = (
  campaign: { assignedProfileIds?: string[]; curatorIds?: string[] },
  accountProfileIds: string[],
  activeProfileId: string
): string => {
  const accountIdSet = new Set(accountProfileIds);
  const assignedIds = (campaign.assignedProfileIds || campaign.curatorIds || [])
    .map(id => id?.toString())
    .filter((id): id is string => Boolean(id && accountIdSet.has(id)));

  if (assignedIds.includes(activeProfileId)) {
    return activeProfileId;
  }

  return assignedIds[0] || activeProfileId;
};

const profileNeedsLiveOwnedFetch = (profile: LinkedCuratorProfile): boolean => {
  const offer = profile.curatorOffer;
  if (offer?.includeAllOwnedPlaylists === true) {
    return true;
  }
  const savedPlaylists = offer?.submissionPlaylists || [];
  const savedIds = offer?.submissionPlaylistIds || [];
  return savedPlaylists.length === 0 && savedIds.length === 0;
};

const savedOwnedSnapshot = (profile: LinkedCuratorProfile): CuratorOfferSubmissionPlaylist[] => {
  const offer = profile.curatorOffer;
  const savedPlaylists = offer?.submissionPlaylists || [];
  if (savedPlaylists.length > 0) {
    return savedPlaylists;
  }
  const savedIds = offer?.submissionPlaylistIds || [];
  return savedIds.map(id => ({ playlistId: id, name: id }));
};

const appendProfilePlaylistOptions = (
  options: CampaignAcceptPlaylistOption[],
  seenPlaylistIds: Set<string>,
  profile: LinkedCuratorProfile,
  selectedPlaylists: CuratorOfferSubmissionPlaylist[]
) => {
  for (const playlist of selectedPlaylists) {
    if (!playlist.playlistId || seenPlaylistIds.has(playlist.playlistId)) continue;
    seenPlaylistIds.add(playlist.playlistId);
    options.push({
      playlistId: playlist.playlistId,
      name: playlist.name,
      profileId: profile.id,
      profileUsername: profile.username,
      imageUrl: playlist.imageUrl,
      externalUrl: playlist.externalUrl,
    });
  }
};

const fetchOwnedPlaylistsGroupedByProfile = async (
  profileIds: string[],
  accountUserId: string
): Promise<Map<string, CuratorOfferSubmissionPlaylist[]>> => {
  const grouped = new Map<string, CuratorOfferSubmissionPlaylist[]>();
  const profileIdSet = new Set(profileIds);

  try {
    const allAccountPlaylists = await fetchAccountOwnedPlaylists(accountUserId);
    for (const item of allAccountPlaylists) {
      if (!profileIdSet.has(item.profileId)) continue;
      const existing = grouped.get(item.profileId) || [];
      existing.push({
        playlistId: item.playlistId,
        name: item.name,
        imageUrl: item.imageUrl,
        externalUrl: item.externalUrl,
      });
      grouped.set(item.profileId, existing);
    }
  } catch (batchError) {
    console.warn('Batch owned playlist fetch for campaign failed:', batchError);
  }

  const missingProfileIds = profileIds.filter(
    profileId => !grouped.has(profileId) || grouped.get(profileId)!.length === 0
  );

  if (missingProfileIds.length > 0) {
    await Promise.all(
      missingProfileIds.map(async profileId => {
        try {
          grouped.set(profileId, await fetchProfileOwnedPlaylists(profileId, accountUserId));
        } catch {
          grouped.set(profileId, []);
        }
      })
    );
  }

  return grouped;
};

const sortPlaylistOptions = (options: CampaignAcceptPlaylistOption[]) =>
  options.sort((a, b) => {
    const profileCompare = a.profileUsername.localeCompare(b.profileUsername);
    if (profileCompare !== 0) return profileCompare;
    return a.name.localeCompare(b.name);
  });

export const loadCampaignAcceptPlaylists = async (
  campaign: { assignedProfileIds?: string[]; curatorIds?: string[] },
  linkedProfiles: LinkedCuratorProfile[],
  accountUserId: string
): Promise<CampaignAcceptPlaylistOption[]> => {
  const profileIds = getCampaignEligibleProfileIds(campaign, linkedProfiles);
  const profileMap = new Map(linkedProfiles.map(profile => [profile.id, profile]));
  const options: CampaignAcceptPlaylistOption[] = [];
  const seenPlaylistIds = new Set<string>();
  const profilesNeedingLiveFetch: LinkedCuratorProfile[] = [];

  for (const profileId of profileIds) {
    const profile = profileMap.get(profileId);
    if (!profile) continue;

    if (profileNeedsLiveOwnedFetch(profile)) {
      profilesNeedingLiveFetch.push(profile);
      continue;
    }

    const { selectedPlaylists } = resolveInitialProfilePlaylists(
      savedOwnedSnapshot(profile),
      profile.curatorOffer
    );
    appendProfilePlaylistOptions(options, seenPlaylistIds, profile, selectedPlaylists);
  }

  if (profilesNeedingLiveFetch.length > 0) {
    const ownedByProfileId = await fetchOwnedPlaylistsGroupedByProfile(
      profilesNeedingLiveFetch.map(profile => profile.id),
      accountUserId
    );

    for (const profile of profilesNeedingLiveFetch) {
      const owned =
        ownedByProfileId.get(profile.id) || profile.curatorOffer?.submissionPlaylists || [];
      const { selectedPlaylists } = resolveInitialProfilePlaylists(owned, profile.curatorOffer);
      appendProfilePlaylistOptions(options, seenPlaylistIds, profile, selectedPlaylists);
    }
  }

  return sortPlaylistOptions(options);
};

export const loadAccountOwnedPlaylistOptions = async (
  linkedProfiles: LinkedCuratorProfile[],
  accountUserId: string
): Promise<CampaignAcceptPlaylistOption[]> => {
  try {
    return await fetchAccountOwnedPlaylists(accountUserId);
  } catch (batchError) {
    console.warn('Batch account playlist fetch failed, falling back to per-profile:', batchError);
  }

  const results = await Promise.all(
    linkedProfiles.map(async profile => {
      try {
        const owned = await fetchProfileOwnedPlaylists(profile.id, accountUserId);
        return { profile, owned };
      } catch {
        return { profile, owned: [] as CuratorOfferSubmissionPlaylist[] };
      }
    })
  );

  const options: CampaignAcceptPlaylistOption[] = [];
  const seenPlaylistIds = new Set<string>();

  for (const { profile, owned } of results) {
    for (const playlist of owned) {
      if (!playlist.playlistId || seenPlaylistIds.has(playlist.playlistId)) continue;
      seenPlaylistIds.add(playlist.playlistId);
      options.push({
        playlistId: playlist.playlistId,
        name: playlist.name,
        profileId: profile.id,
        profileUsername: profile.username,
        imageUrl: playlist.imageUrl,
        externalUrl: playlist.externalUrl,
      });
    }
  }

  return sortPlaylistOptions(options);
};

export const loadProfileSpotifySessions = async (
  profileIds: string[],
  accountUserId: string,
  activeProfileId?: string | null,
  activeSession?: Pick<ProfileSpotifySession, 'accessToken' | 'spotifyUserId'>
): Promise<Record<string, ProfileSpotifySession>> => {
  const sessions: Record<string, ProfileSpotifySession> = {};

  await Promise.all(
    profileIds.map(async profileId => {
      if (activeProfileId && profileId === activeProfileId && activeSession) {
        sessions[profileId] = {
          profileId,
          accessToken: activeSession.accessToken,
          spotifyUserId: activeSession.spotifyUserId,
        };
        return;
      }

      try {
        const session = await fetchProfileSpotifySession(profileId, accountUserId);
        sessions[profileId] = {
          profileId,
          accessToken: session.accessToken,
          spotifyUserId: session.spotifyUserId,
        };
      } catch (error) {
        console.error(`Failed to load Spotify session for profile ${profileId}:`, error);
      }
    })
  );

  return sessions;
};
